import type * as PTimeoutTypes from 'p-timeout'
import type {
  EventSourceParseCallback,
  EventSourceParser
} from 'eventsource-parser'
import fs from 'fs'
import type { Page } from 'puppeteer'
import { remark } from 'remark'
import stripMarkdown from 'strip-markdown'

import * as types from './types'

declare global {
  function ChatGPTAPIBrowserOnProgress(
    partialChatResponse: types.ChatResponse
  ): Promise<void>
}

export function markdownToText(markdown?: string): string {
  return remark()
    .use(stripMarkdown)
    .processSync(markdown ?? '')
    .toString()
}

export async function minimizePage(page: Page) {
  const session = await page.target().createCDPSession()
  const goods = await session.send('Browser.getWindowForTarget')
  const { windowId } = goods
  await session.send('Browser.setWindowBounds', {
    windowId,
    bounds: { windowState: 'minimized' }
  })
}

export async function deleteFolderRecursive(path: string) {
  if (fs.existsSync(path)) {
    fs.readdirSync(path).forEach(function (file, index) {
      var curPath = path + '/' + file
      if (fs.lstatSync(curPath).isDirectory()) {
        deleteFolderRecursive(curPath)
      } else {
        fs.unlinkSync(curPath)
      }
    })
    fs.rmdirSync(path)
  }
}

export async function maximizePage(page: Page) {
  const session = await page.target().createCDPSession()
  const goods = await session.send('Browser.getWindowForTarget')
  const { windowId } = goods
  await session.send('Browser.setWindowBounds', {
    windowId,
    bounds: { windowState: 'normal' }
  })
}

export function isRelevantRequest(url: string): boolean {
  let pathname: string

  try {
    const parsedUrl = new URL(url)
    pathname = parsedUrl.pathname
    url = parsedUrl.toString()
  } catch (_) {
    return false
  }

  if (!url.startsWith('https://chatgpt.com')) {
    return false
  }

  if (
    !pathname.startsWith('/backend-api/') &&
    !pathname.startsWith('/api/auth/session')
  ) {
    return false
  }

  if (pathname.endsWith('backend-api/moderations')) {
    return false
  }

  return true
}

export async function getAccessToken() {
  const access = await fetch('https://chatgpt.com/api/auth/session')
  const { value: reqBody } = await access.body.getReader().read()
  const { accessToken } = JSON.parse(new TextDecoder().decode(reqBody))
  return accessToken
}
// token获取
export async function getRequirementToken(token: string, accessToken?: string) {
  const requirements = await fetch(
    'https://chatgpt.com/backend-api/sentinel/chat-requirements',
    {
      method: 'POST',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        'content-type': 'application/json'
      },
      body: JSON.stringify({ p: token })
    }
  )
  const { value: reqBody } = await requirements.body.getReader().read()
  const requirement = JSON.parse(new TextDecoder().decode(reqBody))
  return requirement
}

// 获取wss_url
export async function getWssUrl(accessToken: string) {
  const requirements = await fetch(
    'https://chatgpt.com/backend-api/register-websocket',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'content-type': 'application/json'
      }
    }
  )
  const { value: reqBody } = await requirements.body.getReader().read()
  const requirement = JSON.parse(new TextDecoder().decode(reqBody))
  return requirement.wss_url
}

// 获取上传附件和聊天信息
export async function getGptMessages(
  messages: string | types.content,
  accessToken: string
) {
  let parts: any[] = []
  let attachments: any = []
  if (typeof messages === 'string') {
    parts.push(messages)
  } else {
    for (let index = 0; index < messages.length; index++) {
      const element = messages[index]
      if (element.type === 'text') {
        parts.push(element.text)
      } else if (element.type === 'image_url') {
        const file = dataURLtoFile(element.image_url.url)
        const filesRes = await fetch('https://chatgpt.com/backend-api/files', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            file_name: file.name,
            file_size: file.size,
            use_case:
              file.type.split('/')[0] === 'image' ? 'multimodal' : 'my_files'
          })
        })
        const { value: fileBody } = await filesRes.body.getReader().read()
        const fileBodyStr = new TextDecoder().decode(fileBody)
        const { file_id, status, upload_url } = JSON.parse(fileBodyStr)
        if (status === 'success') {
          await fetch(upload_url, {
            method: 'PUT',
            headers: {
              'content-type': file.type,
              'x-ms-blob-type': 'BlockBlob'
            },
            body: file
          })
          const uploadedRes = await fetch(
            `https://chatgpt.com/backend-api/files/${file_id}/uploaded`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'content-type': 'application/json'
              }
            }
          )

          if (file.type.indexOf('image') !== -1) {
            const { width, height } = await getImageDimensions(file)
            parts.unshift({
              asset_pointer: `file-service://${file_id}`,
              content_type: 'image_asset_pointer',
              height: height,
              size_bytes: file.size,
              width: width
            })
            attachments.push({
              id: file_id,
              mimeType: file.type,
              name: file.name,
              height: height,
              size: file.size,
              width: width
            })
          } else {
            attachments.push({
              id: file_id,
              mimeType: file.type,
              name: file.name,
              size: file.size
            })
          }
        }
      }
    }
  }
  return {
    parts,
    attachments
  }
  function dataURLtoFile(dataUrl: string) {
    let arr = dataUrl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    const filename = `${Date.now()}.${mime.split('/')[1]}`
    return new File([u8arr], filename, { type: mime })
  }
  function getImageDimensions(
    file: File
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = function (event) {
        const img = new Image()
        img.onload = function () {
          const width = img.width
          const height = img.height
          resolve({ width, height })
        }
        img.onerror = function () {
          reject(new Error('Failed to load image'))
        }
        img.src = event.target.result as string
      }
      reader.onerror = function () {
        reject(new Error('Failed to read file'))
      }
      reader.readAsDataURL(file)
    })
  }
}

/**
 * This function is injected into the ChatGPT webapp page using puppeteer. It
 * has to be fully self-contained, so we copied a few third-party sources and
 * included them in here.
 */
export async function browserPostEventStream(
  url: string,
  accessToken: string,
  body: types.ConversationJSONBody,
  requirement,
  powToken: string,
  wssUrl?: string,
  arkoseToken?: string,
  timeoutMs?: number
): Promise<types.ChatError | types.ChatResponse> {
  // Workaround for https://github.com/esbuild-kit/tsx/issues/113
  globalThis.__name = () => undefined

  class TimeoutError extends Error {
    readonly name: 'TimeoutError'

    constructor(message) {
      super(message)
      this.name = 'TimeoutError'
    }
  }

  /**
    An error to be thrown when the request is aborted by AbortController.
    DOMException is thrown instead of this Error when DOMException is available.
  */
  class AbortError extends Error {
    constructor(message) {
      super()
      this.name = 'AbortError'
      this.message = message
    }
  }

  function getCookie(key: string) {
    const cookieMap = new Map()
    document.cookie.split('; ').forEach((cookie) => {
      const [key, value] = cookie.split('=')
      cookieMap.set(key, value)
    })
    return cookieMap.get(key)
  }

  const BOM = [239, 187, 191]

  let conversationId: string = body?.conversation_id
  const origMessageId = body?.messages?.[body?.messages?.length - 1]?.id
  let messageId: string = body?.messages?.[body?.messages?.length - 1]?.id
  let response = ''
  let socket = null
  if (wssUrl) {
    socket = new WebSocket(wssUrl, 'json.reliable.webpubsub.azure.v1')
  }
  try {
    let abortController: AbortController = null
    if (timeoutMs) {
      abortController = new AbortController()
    }

    const responseP = new Promise<types.ChatResponse>(
      async (resolve, reject) => {
        const connectWebSocket = new Promise((resolve, reject) => {
          socket.onmessage = (event) => {
            resolve(event)
            const data = JSON.parse(event.data)
            if (data?.data?.body) {
              let raw = window.atob(data?.data?.body)
              const partsRegex = /"parts": \[(.*?)\]/
              const matches = raw.match(partsRegex)
              const isParts = matches ? matches[1] : 'null'
              if (isParts.length > 3) {
                onMessage(raw.substring(5).trim())
              }
            }
          }
          socket.onerror = (event) => {
            console.log('socket error', event)
            reject()
          }
        })
        if (socket) await connectWebSocket

        const res = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          signal: abortController?.signal,
          headers: {
            accept: 'text/event-stream',
            'Oai-Device-Id': getCookie('oai-did'),
            'Oai-Language': 'en-US',
            ...(arkoseToken && { 'Openai-Sentinel-Arkose-Token': arkoseToken }),
            'Openai-Sentinel-Chat-Requirements-Token': requirement.token,
            ...(accessToken && { authorization: `Bearer ${accessToken}` }),
            ...(powToken && { 'Openai-Sentinel-Proof-Token': powToken }),
            'content-type': 'application/json'
          }
        })

        console.log('browserPostEventStream response', res)

        if (!res.ok) {
          return {
            error: {
              message: `ChatGPTAPI error ${res.status || res.statusText}`,
              statusCode: res.status,
              statusText: res.statusText
            },
            conversationId,
            messageId
          }
        }
        async function onMessage(data: string) {
          if (data === '[DONE]') {
            if (socket) socket.close()
            return resolve({
              response,
              messageId
            })
          }

          let convoResponseEvent: types.ConversationResponseEvent
          try {
            convoResponseEvent = JSON.parse(data)
          } catch (err) {
            console.warn(
              'warning: chatgpt even stream parse error',
              err.toString(),
              data
            )
            return
          }

          if (!convoResponseEvent) {
            return
          }
          if (
            convoResponseEvent?.message?.recipient &&
            convoResponseEvent?.message?.recipient !== 'all'
          ) {
            return
          }

          if (convoResponseEvent?.message?.author.role === 'user') return

          try {
            if (convoResponseEvent.conversation_id) {
              conversationId = convoResponseEvent.conversation_id
            }

            if (convoResponseEvent.message?.id) {
              messageId = convoResponseEvent.message.id
            }

            const partialResponse: any =
              convoResponseEvent.message?.content?.parts?.[0]
            if (partialResponse) {
              response = partialResponse

              if (
                typeof partialResponse === 'object' &&
                partialResponse!.content_type === 'image_asset_pointer'
              ) {
                try {
                  const imageDownload = await fetch(
                    `https://chatgpt.com/backend-api/files/${
                      partialResponse.asset_pointer.split('//')[1]
                    }/download`,
                    {
                      method: 'GET',
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'content-type': 'application/json'
                      }
                    }
                  )
                  const { value: reqBody } = await imageDownload.body
                    .getReader()
                    .read()
                  const reqStr = new TextDecoder().decode(reqBody)
                  response = `![](${JSON.parse(reqStr).download_url})`
                } catch (error) {}
              }
              if (window.ChatGPTAPIBrowserOnProgress) {
                const isHistory = !!body.messages.find(
                  (item) => item.id === messageId
                )
                if (!isHistory) {
                  const partialChatResponse = {
                    origMessageId,
                    response,
                    conversationId,
                    messageId
                  }
                  await window.ChatGPTAPIBrowserOnProgress(partialChatResponse)
                }
              }
            }
          } catch (err) {
            console.warn('fetchSSE onMessage unexpected error', err)
            reject(err)
          }
        }

        const parser = createParser((event) => {
          if (event.type === 'event') {
            onMessage(event.data)
          }
        })

        for await (const chunk of streamAsyncIterable(res.body)) {
          const str = new TextDecoder().decode(chunk)
          if (str.indexOf('wss_url') === -1) {
            parser.feed(str)
          }
        }
      }
    )

    if (timeoutMs) {
      if (abortController) {
        // This will be called when a timeout occurs in order for us to forcibly
        // ensure that the underlying HTTP request is aborted.
        ;(responseP as any).cancel = () => {
          abortController.abort()
        }
      }

      return await pTimeout(responseP, {
        milliseconds: timeoutMs,
        message: 'ChatGPT timed out waiting for response'
      })
    } else {
      return await responseP
    }
  } catch (err) {
    const errMessageL = err.toString().toLowerCase()

    if (
      response &&
      (errMessageL === 'error: typeerror: terminated' ||
        errMessageL === 'typeerror: terminated')
    ) {
      // OpenAI sometimes forcefully terminates the socket from their end before
      // the HTTP request has resolved cleanly. In my testing, these cases tend to
      // happen when OpenAI has already send the last `response`, so we can ignore
      // the `fetch` error in this case.
      return {
        response,
        conversationId,
        messageId
      }
    }

    return {
      error: {
        message: err.toString(),
        statusCode: err.statusCode || err.status || err.response?.statusCode,
        statusText: err.statusText || err.response?.statusText
      },
      conversationId,
      messageId
    }
  }

  async function* streamAsyncIterable<T>(stream: ReadableStream<T>) {
    const reader = stream.getReader()
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          return
        }
        yield value
      }
    } finally {
      reader.releaseLock()
    }
  }

  // @see https://github.com/rexxars/eventsource-parser
  function createParser(onParse: EventSourceParseCallback): EventSourceParser {
    // Processing state
    let isFirstChunk: boolean
    let buffer: string
    let startingPosition: number
    let startingFieldLength: number

    // Event state
    let eventId: string | undefined
    let eventName: string | undefined
    let data: string

    reset()
    return { feed, reset }

    function reset(): void {
      isFirstChunk = true
      buffer = ''
      startingPosition = 0
      startingFieldLength = -1

      eventId = undefined
      eventName = undefined
      data = ''
    }

    function feed(chunk: string): void {
      buffer = buffer ? buffer + chunk : chunk

      // Strip any UTF8 byte order mark (BOM) at the start of the stream.
      // Note that we do not strip any non - UTF8 BOM, as eventsource streams are
      // always decoded as UTF8 as per the specification.
      if (isFirstChunk && hasBom(buffer)) {
        buffer = buffer.slice(BOM.length)
      }

      isFirstChunk = false

      // Set up chunk-specific processing state
      const length = buffer.length
      let position = 0
      let discardTrailingNewline = false

      // Read the current buffer byte by byte
      while (position < length) {
        // EventSource allows for carriage return + line feed, which means we
        // need to ignore a linefeed character if the previous character was a
        // carriage return
        // @todo refactor to reduce nesting, consider checking previous byte?
        // @todo but consider multiple chunks etc
        if (discardTrailingNewline) {
          if (buffer[position] === '\n') {
            ++position
          }
          discardTrailingNewline = false
        }

        let lineLength = -1
        let fieldLength = startingFieldLength
        let character: string

        for (
          let index = startingPosition;
          lineLength < 0 && index < length;
          ++index
        ) {
          character = buffer[index]
          if (character === ':' && fieldLength < 0) {
            fieldLength = index - position
          } else if (character === '\r') {
            discardTrailingNewline = true
            lineLength = index - position
          } else if (character === '\n') {
            lineLength = index - position
          }
        }

        if (lineLength < 0) {
          startingPosition = length - position
          startingFieldLength = fieldLength
          break
        } else {
          startingPosition = 0
          startingFieldLength = -1
        }

        parseEventStreamLine(buffer, position, fieldLength, lineLength)

        position += lineLength + 1
      }

      if (position === length) {
        // If we consumed the entire buffer to read the event, reset the buffer
        buffer = ''
      } else if (position > 0) {
        // If there are bytes left to process, set the buffer to the unprocessed
        // portion of the buffer only
        buffer = buffer.slice(position)
      }
    }

    function parseEventStreamLine(
      lineBuffer: string,
      index: number,
      fieldLength: number,
      lineLength: number
    ) {
      if (lineLength === 0) {
        // We reached the last line of this event
        if (data.length > 0) {
          onParse({
            type: 'event',
            id: eventId,
            event: eventName || undefined,
            data: data.slice(0, -1) // remove trailing newline
          })

          data = ''
          eventId = undefined
        }
        eventName = undefined
        return
      }

      const noValue = fieldLength < 0
      const field = lineBuffer.slice(
        index,
        index + (noValue ? lineLength : fieldLength)
      )
      let step = 0

      if (noValue) {
        step = lineLength
      } else if (lineBuffer[index + fieldLength + 1] === ' ') {
        step = fieldLength + 2
      } else {
        step = fieldLength + 1
      }

      const position = index + step
      const valueLength = lineLength - step
      const value = lineBuffer
        .slice(position, position + valueLength)
        .toString()

      if (field === 'data') {
        data += value ? `${value}\n` : '\n'
      } else if (field === 'event') {
        eventName = value
      } else if (field === 'id' && !value.includes('\u0000')) {
        eventId = value
      } else if (field === 'retry') {
        const retry = parseInt(value, 10)
        if (!Number.isNaN(retry)) {
          onParse({ type: 'reconnect-interval', value: retry })
        }
      }
    }
  }

  function hasBom(buffer: string) {
    return BOM.every(
      (charCode: number, index: number) => buffer.charCodeAt(index) === charCode
    )
  }

  /**
    TODO: Remove AbortError and just throw DOMException when targeting Node 18.
   */
  function getDOMException(errorMessage) {
    return globalThis.DOMException === undefined
      ? new AbortError(errorMessage)
      : new DOMException(errorMessage)
  }

  /**
    TODO: Remove below function and just 'reject(signal.reason)' when targeting Node 18.
   */
  function getAbortedReason(signal) {
    const reason =
      signal.reason === undefined
        ? getDOMException('This operation was aborted.')
        : signal.reason

    return reason instanceof Error ? reason : getDOMException(reason)
  }

  // @see https://github.com/sindresorhus/p-timeout
  function pTimeout<ValueType, ReturnType = ValueType>(
    promise: PromiseLike<ValueType>,
    options: PTimeoutTypes.Options<ReturnType>
  ): PTimeoutTypes.ClearablePromise<ValueType | ReturnType> {
    const {
      milliseconds,
      fallback,
      message,
      customTimers = { setTimeout, clearTimeout }
    } = options

    let timer: number

    const cancelablePromise = new Promise((resolve, reject) => {
      if (typeof milliseconds !== 'number' || Math.sign(milliseconds) !== 1) {
        throw new TypeError(
          `Expected \`milliseconds\` to be a positive number, got \`${milliseconds}\``
        )
      }

      if (milliseconds === Number.POSITIVE_INFINITY) {
        resolve(promise)
        return
      }

      if (options.signal) {
        const { signal } = options
        if (signal.aborted) {
          reject(getAbortedReason(signal))
        }

        signal.addEventListener('abort', () => {
          reject(getAbortedReason(signal))
        })
      }

      timer = customTimers.setTimeout.call(
        undefined,
        () => {
          if (fallback) {
            try {
              resolve(fallback())
            } catch (error) {
              reject(error)
            }

            return
          }

          const errorMessage =
            typeof message === 'string'
              ? message
              : `Promise timed out after ${milliseconds} milliseconds`
          const timeoutError =
            message instanceof Error ? message : new TimeoutError(errorMessage)

          if (typeof (promise as any).cancel === 'function') {
            ;(promise as any).cancel()
          }

          reject(timeoutError)
        },
        milliseconds
      )
      ;(async () => {
        try {
          resolve(await promise)
        } catch (error) {
          reject(error)
        } finally {
          customTimers.clearTimeout.call(undefined, timer)
        }
      })()
    })

    ;(cancelablePromise as any).clear = () => {
      customTimers.clearTimeout.call(undefined, timer)
      timer = undefined
    }

    return cancelablePromise as any
  }
}
